import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/unsubscribe")({
  head: () => ({
    meta: [
      { title: "Se désabonner — Glow Up Coach" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: UnsubscribePage,
});

type State =
  | { phase: "loading" }
  | { phase: "ready" }
  | { phase: "already" }
  | { phase: "invalid" }
  | { phase: "submitting" }
  | { phase: "done" }
  | { phase: "error"; message: string };

function UnsubscribePage() {
  const [token, setToken] = useState<string | null>(null);
  const [state, setState] = useState<State>({ phase: "loading" });

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("token");
    setToken(t);
    if (!t) {
      setState({ phase: "invalid" });
      return;
    }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(t)}`)
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) return setState({ phase: "invalid" });
        if (body?.valid) return setState({ phase: "ready" });
        if (body?.reason === "already_unsubscribed") return setState({ phase: "already" });
        setState({ phase: "invalid" });
      })
      .catch(() => setState({ phase: "invalid" }));
  }, []);

  const confirm = async () => {
    if (!token) return;
    setState({ phase: "submitting" });
    try {
      const r = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) return setState({ phase: "error", message: body?.error ?? "Erreur" });
      if (body?.success) return setState({ phase: "done" });
      if (body?.reason === "already_unsubscribed") return setState({ phase: "already" });
      setState({ phase: "error", message: "Erreur inattendue" });
    } catch (e: any) {
      setState({ phase: "error", message: e?.message ?? "Erreur réseau" });
    }
  };

  return (
    <main className="min-h-screen bg-background text-foreground flex items-center justify-center px-6 py-16">
      <div className="max-w-md w-full bg-card border border-foreground/10 rounded-3xl p-8 text-center">
        <h1 className="font-display font-bold text-2xl mb-3">Se désabonner</h1>
        {state.phase === "loading" && (
          <p className="text-sm text-muted-foreground">Vérification du lien…</p>
        )}
        {state.phase === "invalid" && (
          <p className="text-sm text-red-500">Lien invalide ou expiré.</p>
        )}
        {state.phase === "already" && (
          <p className="text-sm text-muted-foreground">
            Cette adresse est déjà désabonnée. Aucun email ne vous sera envoyé.
          </p>
        )}
        {state.phase === "ready" && (
          <>
            <p className="text-sm text-muted-foreground mb-6">
              Confirmez-vous vouloir vous désabonner des emails de Glow Up Coach ?
            </p>
            <button
              onClick={confirm}
              className="bg-foreground text-background px-5 py-2.5 rounded-xl text-sm font-semibold hover:opacity-90"
            >
              Confirmer le désabonnement
            </button>
          </>
        )}
        {state.phase === "submitting" && (
          <p className="text-sm text-muted-foreground">Traitement…</p>
        )}
        {state.phase === "done" && (
          <p className="text-sm text-green-600 font-semibold">
            Vous êtes désabonné. Vous ne recevrez plus d'emails.
          </p>
        )}
        {state.phase === "error" && (
          <p className="text-sm text-red-500">{state.message}</p>
        )}
      </div>
    </main>
  );
}